import React, { useState, useEffect } from 'react';
import { Gift, Mail, Send, CreditCard, Check, ChevronRight, Heart, Truck, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';
import api from '../services/api';

const VOUCHER_AMOUNTS = [
  { key: 'voucher_25', amount: 25, popular: false },
  { key: 'voucher_50', amount: 50, popular: false },
  { key: 'voucher_75', amount: 75, popular: false },
  { key: 'voucher_100', amount: 100, popular: true },
  { key: 'voucher_150', amount: 150, popular: false },
  { key: 'voucher_200', amount: 200, popular: false },
  { key: 'voucher_250', amount: 250, popular: false },
  { key: 'voucher_500', amount: 500, popular: false },
];

const MIN_CUSTOM_AMOUNT = 10;
const MAX_CUSTOM_AMOUNT = 1000;

const GiftVouchers = () => {
  const [step, setStep] = useState(1);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState('email');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    recipientName: '',
    recipientEmail: '',
    senderName: '',
    senderEmail: '',
    message: '',
    postalAddress: '',
  });

  // Check for cancelled payment
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cancelled') === 'true') {
      setError('Payment was cancelled. Please try again.');
      // Clear the URL parameter
      window.history.replaceState({}, '', '/gift-vouchers');
    }
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and decimal point
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setCustomAmount(value);
      if (value) {
        setSelectedAmount(null);
        setUseCustomAmount(true);
      }
    }
  };

  const handlePresetAmountClick = (voucher) => {
    setSelectedAmount(voucher);
    setCustomAmount('');
    setUseCustomAmount(false);
  };

  const getDisplayAmount = () => {
    if (useCustomAmount && customAmount) {
      return parseFloat(customAmount);
    }
    return selectedAmount?.amount || 0;
  };

  const isValidCustomAmount = () => {
    if (!customAmount) return false;
    const amount = parseFloat(customAmount);
    return amount >= MIN_CUSTOM_AMOUNT && amount <= MAX_CUSTOM_AMOUNT;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const displayAmount = getDisplayAmount();

    try {
      const requestData = {
        recipient_name: formData.recipientName,
        recipient_email: formData.recipientEmail,
        sender_name: formData.senderName,
        sender_email: formData.senderEmail,
        message: formData.message || null,
        delivery_method: deliveryMethod,
        postal_address: deliveryMethod === 'post' ? formData.postalAddress : null,
        origin_url: window.location.origin,
      };

      // Add either preset amount key or custom amount
      if (useCustomAmount) {
        requestData.custom_amount = parseFloat(customAmount);
      } else {
        requestData.amount_key = selectedAmount.key;
      }

      const response = await api.post('/gift-vouchers/checkout', requestData);

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || 'An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const canProceedToStep2 = selectedAmount !== null || (useCustomAmount && isValidCustomAmount());
  const canProceedToStep3 = formData.recipientName && formData.recipientEmail && formData.senderName && formData.senderEmail;
  const canSubmit = canProceedToStep3 && (deliveryMethod === 'email' || formData.postalAddress);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="gift-vouchers-page">
      <SEO 
        title="Motorhome Hire Gift Vouchers | Suffolk & Essex"
        rawTitle
        url="/gift-vouchers"
        description="Give the gift of adventure with a Millie's Motorhome Hire gift voucher - redeemable against any motorhome hire from our Suffolk base. Perfect for birthdays, anniversaries and Christmas."
        structuredData={generateBreadcrumbSchema([{ name: 'Gift Vouchers', path: '/gift-vouchers' }])}
      />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <div className="inline-flex items-center gap-2 bg-amber-500/20 px-4 py-2 rounded-full mb-6">
            <Gift className="h-5 w-5 text-amber-400" />
            <span className="text-amber-300 font-medium">The Perfect Gift</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Gift Vouchers</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-8">
            Give the gift of adventure! Our gift vouchers are perfect for birthdays, anniversaries, Christmas, or any special occasion.
          </p>
          {/* Voucher Preview Image */}
          <div className="max-w-lg mx-auto">
            <img 
              src="https://customer-assets-v7afamib.emergentagent.net/job_39775d55-8769-4a7d-8ca7-65bbba50c556/artifacts/g5e7ug00_Gift%20voucher.jpeg" 
              alt="Millie's Motorhome Hire Gift Voucher" 
              className="w-full rounded-xl shadow-2xl border-4 border-white/20"
            />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div 
                  className={`flex items-center justify-center w-10 h-10 rounded-full font-bold transition-all ${
                    step >= s ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {step > s ? <Check className="h-5 w-5" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-20 h-1 mx-2 rounded ${step > s ? 'bg-blue-600' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {/* Step 1: Choose Amount */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Choose Your Voucher Amount</h2>
                <p className="text-slate-600">Select a preset amount or enter a custom value</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {VOUCHER_AMOUNTS.map((voucher) => (
                  <button
                    key={voucher.key}
                    onClick={() => handlePresetAmountClick(voucher)}
                    className={`relative p-6 rounded-xl border-2 transition-all ${
                      selectedAmount?.key === voucher.key && !useCustomAmount
                        ? 'border-blue-600 bg-blue-50 shadow-lg'
                        : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                    }`}
                    data-testid={`amount-${voucher.amount}`}
                  >
                    {voucher.popular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        Popular
                      </span>
                    )}
                    <p className="text-3xl font-bold text-slate-900">£{voucher.amount}</p>
                    {selectedAmount?.key === voucher.key && !useCustomAmount && (
                      <Check className="absolute top-2 right-2 h-5 w-5 text-blue-600" />
                    )}
                  </button>
                ))}
              </div>

              {/* Custom Amount Section */}
              <Card className={`border-2 transition-all ${useCustomAmount && isValidCustomAmount() ? 'border-blue-600 bg-blue-50' : 'border-slate-200'}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row items-center gap-4">
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="font-semibold text-slate-900 mb-1">Or enter a custom amount</h3>
                      <p className="text-sm text-slate-500">Between £{MIN_CUSTOM_AMOUNT} and £{MAX_CUSTOM_AMOUNT}</p>
                    </div>
                    <div className="relative w-full md:w-48">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-lg font-semibold">£</span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={customAmount}
                        onChange={handleCustomAmountChange}
                        placeholder="Enter amount"
                        className="pl-8 text-lg font-semibold h-12"
                        data-testid="custom-amount"
                      />
                    </div>
                    {useCustomAmount && isValidCustomAmount() && (
                      <Check className="h-6 w-6 text-blue-600 flex-shrink-0" />
                    )}
                  </div>
                  {customAmount && !isValidCustomAmount() && (
                    <p className="text-red-500 text-sm mt-2 text-center md:text-right">
                      Amount must be between £{MIN_CUSTOM_AMOUNT} and £{MAX_CUSTOM_AMOUNT}
                    </p>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end mt-8">
                <Button 
                  onClick={() => setStep(2)} 
                  disabled={!canProceedToStep2}
                  className="px-8"
                  data-testid="next-step-1"
                >
                  Continue <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Recipient Details */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-2">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Recipient & Sender Details</h2>
                <p className="text-slate-600">Tell us who this gift is for</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Heart className="h-5 w-5 text-red-500" />
                      Recipient Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="recipientName">Recipient's Name *</Label>
                      <Input
                        id="recipientName"
                        name="recipientName"
                        value={formData.recipientName}
                        onChange={handleInputChange}
                        placeholder="e.g. John Smith"
                        required
                        data-testid="recipient-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="recipientEmail">Recipient's Email *</Label>
                      <Input
                        id="recipientEmail"
                        name="recipientEmail"
                        type="email"
                        value={formData.recipientEmail}
                        onChange={handleInputChange}
                        placeholder="john@example.com"
                        required
                        data-testid="recipient-email"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Send className="h-5 w-5 text-blue-500" />
                      Your Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="senderName">Your Name *</Label>
                      <Input
                        id="senderName"
                        name="senderName"
                        value={formData.senderName}
                        onChange={handleInputChange}
                        placeholder="e.g. Jane Smith"
                        required
                        data-testid="sender-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="senderEmail">Your Email *</Label>
                      <Input
                        id="senderEmail"
                        name="senderEmail"
                        type="email"
                        value={formData.senderEmail}
                        onChange={handleInputChange}
                        placeholder="jane@example.com"
                        required
                        data-testid="sender-email"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Personal Message (Optional)</CardTitle>
                </CardHeader>
                <CardContent>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Add a personal message to make it extra special..."
                    className="w-full p-3 border border-slate-200 rounded-lg min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="personal-message"
                  />
                </CardContent>
              </Card>

              <div className="flex justify-between mt-8">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="back-step-2">
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(3)} 
                  disabled={!canProceedToStep3}
                  className="px-8"
                  data-testid="next-step-2"
                >
                  Continue <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Delivery Method & Payment */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-3">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Delivery Method & Payment</h2>
                <p className="text-slate-600">Choose how to deliver the voucher</p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">How should we deliver the voucher?</CardTitle>
                </CardHeader>
                <CardContent>
                  <RadioGroup value={deliveryMethod} onValueChange={setDeliveryMethod} className="space-y-4">
                    <div 
                      className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        deliveryMethod === 'email' ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-blue-300'
                      }`}
                      onClick={() => setDeliveryMethod('email')}
                    >
                      <RadioGroupItem value="email" id="email" className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="email" className="flex items-center gap-2 cursor-pointer font-semibold">
                          <Mail className="h-5 w-5 text-blue-600" />
                          Email (Instant PDF)
                        </Label>
                        <p className="text-sm text-slate-600 mt-1">
                          A beautifully designed voucher will be emailed instantly to the recipient.
                        </p>
                        <span className="inline-block mt-2 text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">
                          FREE - Instant Delivery
                        </span>
                      </div>
                    </div>

                    <div 
                      className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        deliveryMethod === 'post' ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-blue-300'
                      }`}
                      onClick={() => setDeliveryMethod('post')}
                    >
                      <RadioGroupItem value="post" id="post" className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="post" className="flex items-center gap-2 cursor-pointer font-semibold">
                          <Truck className="h-5 w-5 text-amber-600" />
                          Post (Physical Voucher)
                        </Label>
                        <p className="text-sm text-slate-600 mt-1">
                          We will print and post a physical voucher card to the address you provide.
                        </p>
                        <span className="inline-block mt-2 text-xs font-medium text-amber-600 bg-amber-100 px-2 py-1 rounded">
                          FREE - Allow 3-5 working days
                        </span>
                      </div>
                    </div>
                  </RadioGroup>

                  {deliveryMethod === 'post' && (
                    <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                      <Label htmlFor="postalAddress" className="font-semibold">Delivery Address *</Label>
                      <textarea
                        id="postalAddress"
                        name="postalAddress"
                        value={formData.postalAddress}
                        onChange={handleInputChange}
                        placeholder="Enter full postal address including postcode..."
                        className="w-full mt-2 p-3 border border-slate-200 rounded-lg min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                        data-testid="postal-address"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Order Summary */}
              <Card className="bg-slate-50">
                <CardHeader>
                  <CardTitle className="text-lg">Order Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Gift Voucher Value</span>
                      <span className="font-semibold">£{getDisplayAmount().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Delivery ({deliveryMethod === 'email' ? 'Email' : 'Post'})</span>
                      <span className="font-semibold text-green-600">FREE</span>
                    </div>
                    <hr className="my-3" />
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span className="text-blue-600">£{getDisplayAmount().toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-between mt-8">
                <Button variant="outline" onClick={() => setStep(2)} data-testid="back-step-3">
                  Back
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={!canSubmit || isLoading}
                  className="px-8 bg-green-600 hover:bg-green-700"
                  data-testid="pay-button"
                >
                  {isLoading ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Pay £{getDisplayAmount().toFixed(2)}
                    </>
                  )}
                </Button>
              </div>

              <p className="text-center text-sm text-slate-500 mt-4">
                <CreditCard className="inline h-4 w-4 mr-1" />
                Secure payment powered by Stripe
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-slate-100">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center text-slate-900 mb-8">How Gift Vouchers Work</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6 text-center">
                <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <Gift className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">1. Purchase</h3>
                <p className="text-sm text-slate-600">Choose an amount and complete secure payment via Stripe</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="font-semibold mb-2">2. Receive</h3>
                <p className="text-sm text-slate-600">Get a unique voucher code via email (instant) or post (3-5 days)</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="bg-amber-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <Check className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="font-semibold mb-2">3. Redeem</h3>
                <p className="text-sm text-slate-600">Quote the code when booking to deduct from the hire cost</p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 p-6 bg-white rounded-xl text-center">
            <p className="text-slate-700">
              <strong>Vouchers are valid for 24 months</strong> from purchase date and can be used against any motorhome hire.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GiftVouchers;
